/**
 * LeaseAnalysisUpload - AI-powered lease PDF analysis component.
 *
 * Uploads a lease PDF for a specific tenant, sends it through AI analysis,
 * and presents extracted reimbursement provisions for review and application.
 *
 * States: idle -> uploading -> analyzing -> preview -> applied
 */

import { useState, useRef, useCallback } from "react";
import { Upload, CheckCircle2, AlertTriangle, FileText, X, Loader2 } from "lucide-react";
import { analyzeTenantLease, type LeaseAnalysisResponse } from "../api/client";
import { friendlyError } from "../utils/errors";

interface LeaseAnalysisUploadProps {
  dealId: string;
  tenantIdx: number;
  tenantName: string;
  onApplied: (update: {
    lease_type: string;
    cam_structure: Record<string, any>;
    pro_rata_share: number | null;
  }) => void;
  onEditApply: (update: {
    lease_type: string;
    cam_structure: Record<string, any>;
    pro_rata_share: number | null;
  }) => void;
}

type UploadState = "idle" | "uploading" | "analyzing" | "preview" | "applied";

const METHOD_LABELS: Record<string, string> = {
  nnn: "NNN (Pro-Rata)",
  base_year_stop: "Base Year Stop",
  modified_gross: "Modified Gross",
  expense_stop: "Expense Stop",
  fixed_cam: "Fixed $/SF",
  gross: "Gross (None)",
  none: "None",
};

function getMethodLabel(method: string): string {
  return METHOD_LABELS[method?.toLowerCase()] || method || "Unknown";
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const pct = Math.round(confidence * 100);
  let color: string;
  let bgColor: string;
  let label: string;
  if (confidence >= 0.85) {
    color = "text-green-700";
    bgColor = "bg-green-50 border-green-200";
    label = "High Confidence";
  } else if (confidence >= 0.70) {
    color = "text-amber-700";
    bgColor = "bg-amber-50 border-amber-200";
    label = "Medium Confidence";
  } else {
    color = "text-red-700";
    bgColor = "bg-red-50 border-red-200";
    label = "Low Confidence";
  }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${bgColor} ${color}`}>
      {confidence >= 0.85 ? (
        <CheckCircle2 className="w-3 h-3" />
      ) : (
        <AlertTriangle className="w-3 h-3" />
      )}
      {label} ({pct}%)
    </span>
  );
}

function PoolCard({
  title,
  pool,
}: {
  title: string;
  pool: Record<string, any>;
}) {
  const method = pool.method || "unknown";
  return (
    <div className="bg-white border border-grove-200 rounded-lg p-3">
      <h5 className="text-[11px] font-semibold uppercase tracking-wider text-bark-500 mb-2">
        {title}
      </h5>
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between">
          <span className="text-bark-400">Method</span>
          <span className="font-medium text-bark-700">{getMethodLabel(method)}</span>
        </div>
        {pool.base_year != null && (
          <div className="flex justify-between">
            <span className="text-bark-400">Base Year</span>
            <span className="font-medium text-bark-700">{pool.base_year}</span>
          </div>
        )}
        {pool.base_amount != null && (
          <div className="flex justify-between">
            <span className="text-bark-400">Base Amount</span>
            <span className="font-medium text-bark-700 tabular-nums">
              ${pool.base_amount.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          </div>
        )}
        {pool.base_amount_psf != null && (
          <div className="flex justify-between">
            <span className="text-bark-400">Base $/SF</span>
            <span className="font-medium text-bark-700 tabular-nums">
              ${pool.base_amount_psf.toFixed(2)}
            </span>
          </div>
        )}
        {pool.cap_rate != null && (
          <div className="flex justify-between">
            <span className="text-bark-400">Cap Rate</span>
            <span className="font-medium text-bark-700 tabular-nums">
              {(pool.cap_rate * 100).toFixed(1)}%
              {pool.cap_type ? ` (${pool.cap_type})` : ""}
            </span>
          </div>
        )}
        {pool.floor_rate != null && (
          <div className="flex justify-between">
            <span className="text-bark-400">Floor</span>
            <span className="font-medium text-bark-700 tabular-nums">
              {(pool.floor_rate * 100).toFixed(1)}%
            </span>
          </div>
        )}
        {pool.admin_fee_pct != null && (
          <div className="flex justify-between">
            <span className="text-bark-400">Admin Fee</span>
            <span className="font-medium text-bark-700 tabular-nums">
              {(pool.admin_fee_pct * 100).toFixed(1)}%
            </span>
          </div>
        )}
        {pool.fixed_cam_psf != null && (
          <div className="flex justify-between">
            <span className="text-bark-400">Fixed $/SF</span>
            <span className="font-medium text-bark-700 tabular-nums">
              ${pool.fixed_cam_psf.toFixed(2)}
            </span>
          </div>
        )}
        {pool.gross_up_provision && (
          <div className="flex justify-between">
            <span className="text-bark-400">Gross-Up</span>
            <span className="font-medium text-bark-700">
              Yes{pool.gross_up_threshold != null ? ` (${(pool.gross_up_threshold * 100).toFixed(0)}%)` : ""}
            </span>
          </div>
        )}
        {pool.special_provisions && (
          <div className="mt-2 text-xs text-bark-500 italic border-t border-grove-100 pt-1.5">
            {pool.special_provisions}
          </div>
        )}
      </div>
    </div>
  );
}

export default function LeaseAnalysisUpload({
  dealId,
  tenantIdx,
  tenantName,
  onApplied,
  onEditApply,
}: LeaseAnalysisUploadProps) {
  const [state, setState] = useState<UploadState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<LeaseAnalysisResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.name.toLowerCase().endsWith(".pdf")) {
        setError("Only PDF files are supported");
        return;
      }

      setError(null);
      setState("uploading");

      try {
        setState("analyzing");
        const result = await analyzeTenantLease(dealId, tenantIdx, file);
        setResponse(result);
        setState("preview");
      } catch (err: any) {
        const msg = friendlyError(
          err?.response?.data?.detail ||
            err?.message ||
            "Couldn't analyze the lease. Try uploading it again."
        );
        setError(msg);
        setState("idle");
      }

      // Reset file input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [dealId, tenantIdx]
  );

  const handleApply = useCallback(() => {
    if (!response) return;
    onApplied(response.suggested_update);
    setState("applied");
  }, [response, onApplied]);

  const handleEditApply = useCallback(() => {
    if (!response) return;
    onEditApply(response.suggested_update);
    setState("applied");
  }, [response, onEditApply]);

  const handleReset = useCallback(() => {
    setState("idle");
    setResponse(null);
    setError(null);
  }, []);

  // Applied state
  if (state === "applied") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
        <CheckCircle2 className="w-4 h-4 text-green-600" />
        <span className="text-sm font-medium text-green-700">Lease Analyzed</span>
        <button
          onClick={handleReset}
          className="ml-auto text-xs text-green-600 hover:text-green-800 underline"
        >
          Re-analyze
        </button>
      </div>
    );
  }

  // Analyzing / uploading state
  if (state === "uploading" || state === "analyzing") {
    return (
      <div className="flex items-center gap-3 px-4 py-3 bg-grove-50 border border-grove-200 rounded-lg">
        <Loader2 className="w-5 h-5 text-forest-600 animate-spin" />
        <div>
          <p className="text-sm font-medium text-bark-700">
            {state === "uploading" ? "Uploading lease..." : "Analyzing lease with AI..."}
          </p>
          <p className="text-xs text-bark-400">
            This typically takes 15-30 seconds
          </p>
        </div>
      </div>
    );
  }

  // Preview state
  if (state === "preview" && response) {
    const analysis = response.analysis;
    return (
      <div className="bg-grove-50 border border-grove-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-grove-200">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-forest-600" />
            <span className="text-sm font-semibold text-bark-700">
              AI Lease Analysis
            </span>
            <ConfidenceBadge confidence={analysis.confidence} />
          </div>
          <button
            onClick={handleReset}
            className="p-1 text-bark-400 hover:text-bark-600 rounded"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Lease type summary */}
        {analysis.lease_type_summary && (
          <div className="px-4 py-2 border-b border-grove-200 bg-white">
            <p className="text-sm text-bark-600">
              <span className="font-medium">Lease Type: </span>
              {analysis.lease_type_summary}
            </p>
          </div>
        )}

        {/* Pro-rata share */}
        {analysis.pro_rata_share != null && (
          <div className="px-4 py-2 border-b border-grove-200 bg-white">
            <p className="text-sm text-bark-600">
              <span className="font-medium">Pro-Rata Share: </span>
              <span className="tabular-nums">{(analysis.pro_rata_share * 100).toFixed(2)}%</span>
            </p>
          </div>
        )}

        {/* Per-category method cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4">
          <PoolCard title="CAM Recovery" pool={analysis.cam_recovery} />
          <PoolCard title="Tax Recovery" pool={analysis.tax_recovery} />
          <PoolCard title="Insurance Recovery" pool={analysis.insurance_recovery} />
        </div>

        {/* Notes */}
        {analysis.notes && analysis.notes.length > 0 && (
          <div className="px-4 pb-3">
            <h5 className="text-[11px] font-semibold uppercase tracking-wider text-bark-500 mb-1">
              Notes
            </h5>
            <ul className="text-xs text-bark-500 space-y-0.5">
              {analysis.notes.map((note, i) => (
                <li key={i} className="flex gap-1">
                  <span className="text-bark-300">-</span>
                  {note}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-3 px-4 py-3 border-t border-grove-200 bg-white">
          <button
            onClick={handleApply}
            className="px-4 py-2 text-sm font-medium text-white bg-forest-600 rounded-lg hover:bg-forest-700 transition-colors"
          >
            Apply to Tenant
          </button>
          <button
            onClick={handleEditApply}
            className="px-4 py-2 text-sm font-medium text-bark-600 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 transition-colors"
          >
            Edit & Apply
          </button>
          <button
            onClick={handleReset}
            className="ml-auto text-sm text-bark-400 hover:text-bark-600"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Idle state
  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        onChange={handleFileSelect}
        className="hidden"
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-forest-600 bg-grove-50 border border-grove-200 rounded-lg hover:bg-grove-100 hover:border-forest-300 transition-colors"
      >
        <Upload className="w-3.5 h-3.5" />
        Analyze Lease
      </button>
      {error && (
        <p className="mt-1.5 text-xs text-red-600">{error}</p>
      )}
    </div>
  );
}
